import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useCurrentUser } from '../hooks/useCurrentUser';
import ReportBugButton from '../components/ReportBugButton';
import {
  LayoutDashboard, FileText, Building2, GraduationCap,
  Users, CreditCard, Settings, LogOut, DollarSign, Megaphone, Phone,
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: null },
  { to: '/bills', icon: FileText, label: 'Bills', roles: ['admin', 'bookkeeper'] },
  { to: '/vendors', icon: Building2, label: 'Vendors', roles: ['admin', 'bookkeeper'] },
  { to: '/cle', icon: GraduationCap, label: 'CLE', roles: ['admin', 'attorney'] },
  { to: '/payroll', icon: DollarSign, label: 'Payroll', roles: null },
  { to: '/employees', icon: Users, label: 'Staff', roles: ['admin'] },
  { to: '/subscriptions', icon: CreditCard, label: 'Firm Ops', roles: ['admin', 'bookkeeper'] },
  { to: '/advertising', icon: Megaphone, label: 'Advertising', roles: ['admin', 'attorney'] },
  { to: '/calls', icon: Phone, label: 'Call Archive', roles: ['admin', 'attorney'] },
  { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
] as const;

const SIDEBAR_WIDTH = 240;

export default function AppLayout() {
  const { logout, authEnabled } = useAuth();
  const { data: user } = useCurrentUser();

  const filteredNav = NAV_ITEMS.filter(item => {
    if (!item.roles) return true;
    return user && (item.roles as readonly string[]).includes(user.role);
  });

  return (
    <div style={{ display: 'flex', minHeight: '100dvh', paddingTop: 3 /* gold accent line */ }}>
      {/* Sidebar */}
      <aside style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        background: 'var(--card)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 3,
        left: 0,
        bottom: 0,
        zIndex: 100,
        boxShadow: '1px 0 4px rgba(0, 0, 0, 0.03)',
      }}>
        {/* Brand */}
        <div style={{
          padding: '1.5rem 1.25rem 1.25rem',
          borderBottom: '1px solid var(--border-light)',
        }}>
          <div style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'var(--primary)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}>
            Sabrina
          </div>
          <div style={{
            fontSize: '0.6rem',
            color: 'var(--accent)',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginTop: 2,
          }}>
            Klein & Sheridan
          </div>
        </div>

        {/* Nav links */}
        <nav style={{
          flex: 1,
          padding: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {filteredNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
            >
              {({ isActive }) => (
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '0.6rem 0.85rem',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.8rem',
                  fontWeight: isActive ? 600 : 450,
                  color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
                  background: isActive ? 'var(--primary-light)' : 'transparent',
                  transition: 'all var(--transition)',
                  letterSpacing: '0.02em',
                }}>
                  <item.icon size={17} strokeWidth={isActive ? 2.2 : 1.8} />
                  {item.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User area at bottom */}
        <div style={{
          padding: '1rem 1.25rem',
          borderTop: '1px solid var(--border-light)',
        }}>
          {user && (
            <div style={{ marginBottom: authEnabled ? 10 : 0 }}>
              <div style={{
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'var(--text)',
              }}>
                {user.name}
              </div>
              <div style={{
                fontSize: '0.6rem',
                color: 'var(--accent)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginTop: 1,
              }}>
                {user.role}
              </div>
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <ReportBugButton />
          </div>
          {authEnabled && (
            <button
              onClick={logout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '0.5rem 0.75rem',
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-muted)',
                fontSize: '0.75rem',
                transition: 'all var(--transition)',
              }}
            >
              <LogOut size={14} />
              Sign out
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main style={{
        flex: 1,
        marginLeft: SIDEBAR_WIDTH,
        padding: '2rem',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
